import client from "../worker/client";

type EventHandler = (data: any) => void;

interface AuthorizeResponse {
  token: string;
}

interface StreamEnvelope {
  channel?: string;
  event?: string;
  data?: any;
}

const REALTIME_AUTHORIZE_PATH = "/api/realtime/authorize";
const REALTIME_STREAM_PATH = "/api/realtime/stream";

class SseChannel {
  private handlers: Map<string, Set<EventHandler>> = new Map();

  constructor(
    private readonly realtimeClient: SsePusherClient,
    public readonly name: string
  ) {}

  bind(eventName: string, handler: EventHandler) {
    const key = eventName || "";
    const handlers = this.handlers.get(key) ?? new Set<EventHandler>();
    handlers.add(handler);
    this.handlers.set(key, handlers);
    this.realtimeClient.scheduleRefresh();
    return this;
  }

  unbind_all() {
    this.handlers.clear();
    this.realtimeClient.scheduleRefresh();
    return this;
  }

  disconnect() {
    this.handlers.clear();
    this.realtimeClient.removeChannel(this.name);
    return this;
  }

  dispatch(eventName: string, data: any) {
    const handlers = this.handlers.get(eventName || "");
    if (!handlers) {
      return;
    }

    handlers.forEach((handler) => handler(data));
  }

  eventNames() {
    return Array.from(this.handlers.keys());
  }
}

class SsePusherClient {
  private channels = new Map<string, SseChannel>();
  private eventSource: EventSource | null = null;
  private reconnectTimer: number | null = null;
  private refreshToken = 0;
  private reconnectDelayMs = 1500;

  subscribe(channelName: string) {
    const normalized = channelName.trim();
    const existing = this.channels.get(normalized);
    if (existing) {
      return existing;
    }

    const channel = new SseChannel(this, normalized);
    this.channels.set(normalized, channel);
    this.scheduleRefresh();
    return channel;
  }

  disconnect() {
    this.channels.clear();
    this.clearReconnectTimer();
    this.closeEventSource();
  }

  removeChannel(channelName: string) {
    this.channels.delete(channelName);
    if (this.channels.size === 0) {
      this.clearReconnectTimer();
      this.closeEventSource();
      return;
    }

    this.scheduleRefresh();
  }

  scheduleRefresh(delayMs = 0) {
    if (typeof window === "undefined") {
      return;
    }

    this.clearReconnectTimer();
    this.reconnectTimer = window.setTimeout(() => {
      void this.refreshStream();
    }, delayMs);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private async refreshStream() {
    if (typeof window === "undefined" || typeof EventSource === "undefined") {
      return;
    }

    this.clearReconnectTimer();

    const requestId = ++this.refreshToken;
    const channels = Array.from(this.channels.keys());

    if (channels.length === 0) {
      this.closeEventSource();
      return;
    }

    try {
      const response = await client
        .post<AuthorizeResponse>(
          buildGatewayUrl(REALTIME_AUTHORIZE_PATH),
          {
            channels,
          }
        )
        .then((result) => result.data);

      if (requestId !== this.refreshToken) {
        return;
      }

      this.openEventSource(response.token);
    } catch (_error) {
      this.closeEventSource();
      this.scheduleRefresh(this.reconnectDelayMs);
      this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 10000);
    }
  }

  private openEventSource(token: string) {
    this.closeEventSource();

    const streamUrl = buildGatewayUrl(
      `${REALTIME_STREAM_PATH}?token=${encodeURIComponent(token)}`
    );
    const source = new EventSource(streamUrl);

    source.onopen = () => {
      this.reconnectDelayMs = 1500;
    };

    source.onerror = () => {
      this.closeEventSource();
      this.scheduleRefresh(this.reconnectDelayMs);
      this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 10000);
    };

    source.onmessage = (event) => {
      this.handleIncomingEvent("", event);
    };

    const eventNames = new Set<string>();
    this.channels.forEach((channel) => {
      channel.eventNames().forEach((eventName) => {
        if (eventName) {
          eventNames.add(eventName);
        }
      });
    });

    eventNames.forEach((eventName) => {
      source.addEventListener(eventName, (event) => {
        this.handleIncomingEvent(eventName, event as MessageEvent);
      });
    });

    this.eventSource = source;
  }

  private handleIncomingEvent(fallbackEventName: string, event: MessageEvent) {
    let envelope: StreamEnvelope;

    try {
      envelope = JSON.parse(event.data);
    } catch (_error) {
      return;
    }

    if (!envelope.channel) {
      return;
    }

    const channel = this.channels.get(envelope.channel);
    if (!channel) {
      return;
    }

    channel.dispatch(envelope.event || fallbackEventName || "", envelope.data);
  }

  private closeEventSource() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}

export function createRealtimePusherClient() {
  return new SsePusherClient();
}

function buildGatewayUrl(path: string) {
  const gatewayBaseUrl = resolveGatewayBaseUrl();
  if (gatewayBaseUrl) {
    return new URL(path, gatewayBaseUrl).toString();
  }

  if (typeof window === "undefined") {
    return path;
  }

  return new URL(path, window.location.origin).toString();
}

function resolveGatewayBaseUrl() {
  const baseURL = client.defaults.baseURL;
  if (!baseURL) {
    return null;
  }

  try {
    if (typeof window === "undefined") {
      return new URL(baseURL, "http://localhost").toString();
    }

    return new URL(baseURL, window.location.origin).toString();
  } catch (_error) {
    return null;
  }
}

